import React from "react";
import { Search, RefreshCw, Plus, Trash2, X, Download, ArrowUpDown } from "lucide-react";

// Bulk-action button color variants — kept as tokens only, no hardcoded colors.
const VARIANT_STYLES = {
  primary: "text-primary bg-primary_tint border-border",
  success: "text-success bg-success_bg border-success_border",
  danger: "text-danger bg-danger_bg border-danger_border",
  info: "text-info bg-info_bg border-info_border",
  default: "text-text_secondary bg-surface border-border",
};

const TableToolBar = ({
  title = "",
  onSearch,
  onRefresh,
  onCreate,
  onDeleteSelected,
  selectedCount = 0,
  resetSearchTrigger,
  canCreate = true,
  searchDebounceMs = 350,
  resultCount,
  sortOptions,
  sortValue,
  onSortChange,
  bulkActions,
  onClearSelection,
  onExport,
  exporting = false,
  advanceFilter,
}) => {
  const [search, setSearch] = React.useState("");
  const debounceRef = React.useRef(null);

  const handleSearch = (value) => {
    setSearch(value);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => onSearch?.(value), searchDebounceMs);
  };

  React.useEffect(() => {
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, []);

  React.useEffect(() => {
    setSearch("");
  }, [resetSearchTrigger]);

  const hasSelection = selectedCount > 0;

  return (
    <div className="bg-surface border border-border rounded-xl shadow-sm">
      {/* Header */}
      {title && (
        <div className="px-4 py-3 border-b border-border">
          <h2 className="text-sm font-semibold text-text_primary tracking-tight">
            {title}
          </h2>
        </div>
      )}

      {/* Toolbar — single row on all screen sizes: search, refresh, sort, bulk actions, export, create */}
      <div className="px-3 sm:px-4 py-3 flex items-center gap-2 w-full min-w-0 flex-wrap sm:flex-nowrap">

        {/* Search */}
        <div className="relative flex-1 min-w-0 sm:flex-none sm:w-64">
          <Search
            size={16}
            className="absolute left-2.5 top-1/2 -translate-y-1/2 text-text_muted"
          />
          <input
            type="text"
            value={search}
            onChange={(e) => handleSearch(e.target.value)}
            placeholder="Search..."
            className="w-full pl-8 pr-3 py-2 text-xs border border-border rounded-md bg-transparent text-text_primary placeholder:text-text_muted focus:outline-none focus:ring-1 focus:ring-primary focus:border-primary transition"
          />
        </div>

        {/* Refresh */}
        <button
          onClick={onRefresh}
          className="shrink-0 inline-flex items-center justify-center px-3 py-2 text-xs font-medium text-text_secondary bg-surface border border-border rounded-md hover:bg-surface_alt hover:text-text_primary transition"
          title="Refresh"
        >
          <RefreshCw size={14} />
        </button>

        {/* Advanced filter — the page passes its own <GlobalAdvanceFilter /> */}
        {advanceFilter}

        {/* Sort */}
        {sortOptions?.length > 0 && (
          <div className="relative shrink-0">
            <ArrowUpDown
              size={13}
              className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-text_muted"
            />
            <select
              value={sortValue}
              onChange={(e) => onSortChange?.(e.target.value)}
              className="cursor-pointer appearance-none pl-7 pr-3 py-2 text-xs font-medium border border-border rounded-md bg-surface text-text_secondary hover:text-text_primary focus:outline-none focus:ring-1 focus:ring-primary focus:border-primary transition"
            >
              {sortOptions.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>
        )}

        {/* Spacer pushes the action buttons to the right on larger screens */}
        <div className="hidden sm:block flex-1" />

        {/* Selection indicator + clear */}
        {hasSelection && (
          <div className="shrink-0 inline-flex items-center gap-1.5 pl-2.5 pr-1.5 py-1 text-xs font-medium text-primary bg-primary_tint border border-border rounded-md">
            {selectedCount} selected
            {onClearSelection && (
              <button
                onClick={onClearSelection}
                className="rounded p-0.5 hover:bg-surface"
                title="Clear selection"
              >
                <X size={12} />
              </button>
            )}
          </div>
        )}

        {/* Bulk actions (preferred) — falls back to the legacy single Delete button */}
        {hasSelection && bulkActions?.length > 0 &&
          bulkActions.map((action) => {
            const Icon = action.icon;
            const styles = VARIANT_STYLES[action.variant] || VARIANT_STYLES.default;
            return (
              <button
                key={action.key}
                onClick={() => action.onClick(selectedCount)}
                className={`shrink-0 inline-flex items-center gap-1.5 px-3 py-2 text-xs font-medium rounded-md border transition ${styles}`}
              >
                {Icon && <Icon size={14} />}
                <span className="hidden sm:inline">{action.label}</span>
              </button>
            );
          })}

        {hasSelection && !bulkActions?.length && onDeleteSelected && (
          <button
            onClick={onDeleteSelected}
            className="shrink-0 inline-flex items-center gap-1.5 px-3 py-2 text-xs font-medium text-danger bg-danger_bg border border-danger_border rounded-md hover:bg-danger/10 transition"
          >
            <Trash2 size={14} />
            <span className="hidden sm:inline">Delete ({selectedCount})</span>
          </button>
        )}

        {/* Export */}
        {onExport && (
          <button
            onClick={onExport}
            disabled={exporting}
            className="shrink-0 inline-flex items-center gap-1.5 px-3 py-2 text-xs font-medium text-text_secondary bg-surface border border-border rounded-md hover:bg-surface_alt hover:text-text_primary transition disabled:opacity-50"
            title="Export"
          >
            <Download size={14} />
            <span className="hidden sm:inline">{exporting ? "Exporting..." : "Export"}</span>
          </button>
        )}

        {/* Create */}
        {canCreate && (
          <button
            onClick={onCreate}
            className="shrink-0 inline-flex items-center gap-1.5 px-3 sm:px-3.5 py-2 text-xs font-medium text-white bg-primary rounded-md hover:bg-primary_hover transition shadow-sm"
          >
            <Plus size={14} />
            <span className="hidden sm:inline">Create</span>
          </button>
        )}
      </div>
    </div>
  );
};

export default TableToolBar;
